import { Component, OnInit, NgZone } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Casautomation } from 'src/app/services/casautomation.service';

declare function CallSmallCase(TranID: string, Token: string, cb: any, errorCb?: any): any;

@Component({
  selector: 'app-wealthLens-report-temp',
  templateUrl: './wealthLens-report-temp.component.html',
  styleUrls: ['./wealthLens-report-temp.component.css']
})
export class WealthLensReportTempComponent implements OnInit {
  name = '';
  email = '';
  mobile = '';
  pan = '';
  reportType = '';
  reportTypeOptions = [
    { label: 'Aggressive', value: 'AGGRESSIVE' },
    { label: 'Moderate', value: 'MODERATE' },
    { label: 'Defensive', value: 'DEFENSIVE' }
  ];

  transactionId = '';
  token = '';
  authToken = '';
  rmLeadCode = '';

  loadingSm = false;
  formSubmitted = false;
  submitted = false;
  alreadySubmitted = false;
  errorMessage = '';
  successMessage = '';

  isModalVisible = false;
  /** Shows the "No current investment found" popup when the report has no data. */
  noDataFound = false;

  private readonly reportRoute = '/wealth-report-view';

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private casService: Casautomation,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.titleService.setTitle('Bajaj Capital WealthLens Report');
    this.metaService.updateTag({
      name: 'description',
      content: 'Get a personalised WealthLens analysis of your mutual fund portfolio in under 60 seconds.'
    });
    this.rmLeadCode = (this.activatedRoute.snapshot.queryParamMap.get('ref') || '').trim();
  }

  // ---------- Modal open / close ----------

  showLeadForm(): void {
    this.openModal();
  }

  openModal(): void {
    this.isModalVisible = true;
    this.formSubmitted = false;
    this.noDataFound = false;
    this.errorMessage = '';
  }

  closeModal(): void {
    this.isModalVisible = false;
    this.noDataFound = false;
    this.errorMessage = '';
  }

  submitModalForm(modalForm: NgForm): void {
    this.formSubmitted = true;

    if (modalForm.invalid) {
      modalForm.form.markAllAsTouched();
      return;
    }

    this.launchMfCentral();
  }

  // ---------- MF Central / smallcase flow ----------

  launchMfCentral(): void {
    this.errorMessage = '';
    this.successMessage = '';
    if (!this.pan || !this.mobile || !this.name || !this.email) return;
    this.loadingSm = true;

    const authPayload = {
      pan: this.pan,
      email: this.email,
      mobile: this.mobile,
    };

    this.casService.generateTokenByPanEmail(authPayload).pipe(
      switchMap((authRes: any) => {
        this.authToken = this.getJwtToken(authRes);

        if (!this.authToken) {
          return throwError(() => new Error('Auth Token Missing'));
        }

        this.saveReportSession();
        return this.casService.getFetchStatus({ pan: this.pan }, this.authToken);
      }),
      switchMap((statusRes: any) => {
        const fetchedStatus = statusRes?.data?.fetchedStatus;

        if (fetchedStatus === true) {
          this.alreadySubmitted = true;
          this.submitted = false;
          this.successMessage =
            'Your query is already submitted. Our RM will contact you soon.';
          this.loadingSm = false;
          this.openReportPage();
          return EMPTY;
        }

        return this.casService.generateTokenSm(this.authToken);
      }),
      switchMap((tokenRes: any) => {
        this.token = tokenRes?.data?.token;
        const payload = {
          smallcaseToken: this.token,
          pan: this.pan,
          mobile: this.mobile,
        };

        return this.casService.generateTransactionSm(payload, this.authToken);
      }),
      catchError((error) => {
        this.loadingSm = false;
        this.errorMessage = error.message === 'Auth Token Missing'
          ? 'Unable to authenticate this request. Please try again.'
          : 'Something went wrong. Please try again.';
        return EMPTY;
      })
    ).subscribe((transactionRes: any) => {
      this.transactionId = transactionRes?.data?.result?.transactionId || transactionRes?.data?.transactionId;

      if (!this.transactionId) {
        this.loadingSm = false;
        this.errorMessage = 'Unable to start report generation. Please try again.';
        return;
      }

      CallSmallCase(
        this.transactionId,
        this.token,
        () => {
          // SDK completes outside Angular's zone; re-enter so the UI updates.
          this.ngZone.run(() => this.verifyDataThenOpenReport());
        },
        (err: any) => {
          // User closed/cancelled the SDK, or the transaction errored.
          this.ngZone.run(() => this.onSmallcaseCancelled(err));
        }
      );
    });
  }

  /**
   * Called when the smallcase SDK is closed/cancelled by the user or the
   * transaction errors out before completing. Reset so the form can be reused.
   */
  private onSmallcaseCancelled(err?: any): void {
    console.warn('Smallcase SDK cancelled or failed', err);
    this.loadingSm = false;
    this.formSubmitted = false;
  }

  /**
   * After the SDK completes we submit the lead, then confirm the customer
   * actually has portfolio data before navigating. If no data is returned we
   * show the "No current investment found" popup instead of the report view.
   */
  private verifyDataThenOpenReport(): void {
    this.insertLead();
    this.checkReportDataAvailability();
  }

  /** Submits the campaign lead (fire-and-forget for navigation purposes). */
  private insertLead(): void {
    const payload: any = {
      name: this.name,
      pan: this.pan,
      email: this.email,
      phone: this.mobile,
      transactionId: this.transactionId,
      source: 'bcl_campaign_mf_wealthLens',
      riskProfile: this.reportType,
    };

    if (this.rmLeadCode) {
      payload['rmLead'] = this.rmLeadCode;
    }

    this.casService.insertMfcampaignlead(payload, this.authToken).subscribe({
      next: () => {
        this.saveReportSession();
      },
      error: (err) => {
        console.error('WealthLens lead submission failed', err);
      },
    });
  }

  /**
   * Checks whether report data exists for this customer. Navigates to the
   * report view only when real data is returned; otherwise surfaces the
   * no-data popup so we never show placeholder numbers.
   */
  private checkReportDataAvailability(): void {
    const panToUse = this.pan;
    const reportType = this.reportType || 'AGGRESSIVE';

    this.casService.getWealthLensReportData({ pan: panToUse, reportType }, this.authToken).subscribe({
      next: (res: any) => {
        const hasData =
          res?.statusCode === 200 &&
          res?.data &&
          Array.isArray(res.data.fundDetails) &&
          res.data.fundDetails.length > 0;

        if (hasData) {
          this.loadingSm = false;
          this.saveReportSession();
          this.openReportPage();
        } else {
          this.showNoDataPopup();
        }
      },
      error: (err) => {
        // 404 / any failure means there is nothing to show.
        console.error('WealthLens report data unavailable', err);
        this.showNoDataPopup();
      },
    });
  }

  /** Shows the "No current investment found" popup and stops the spinner. */
  private showNoDataPopup(): void {
    this.loadingSm = false;
    this.noDataFound = true;
    this.isModalVisible = true;
  }

  /**
   * "OK" on the no-data popup: clear the form and reopen a fresh, empty
   * lead form so the user can try again with different details.
   */
  reloadForm(): void {
    this.noDataFound = false;
    this.resetFormFields();
    this.openModal();
  }

  /** Cross (×) on the no-data popup: close the whole modal and clear the form. */
  closeNoDataPopup(): void {
    this.noDataFound = false;
    this.resetFormFields();
    this.closeModal();
  }

  /** Clears all lead-form fields and transient submission state. */
  private resetFormFields(): void {
    this.name = '';
    this.email = '';
    this.mobile = '';
    this.pan = '';
    this.reportType = '';
    this.formSubmitted = false;
    this.submitted = false;
    this.alreadySubmitted = false;
    this.loadingSm = false;
    this.errorMessage = '';
    this.successMessage = '';
  }

  // ---------- Input guards ----------

  convertPanToUppercase(): void {
    this.pan = this.pan ? this.pan.toUpperCase() : '';
  }

  onlyLetters(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;

    if (
      !(charCode >= 65 && charCode <= 90) &&
      !(charCode >= 97 && charCode <= 122) &&
      charCode !== 32
    ) {
      event.preventDefault();
    }
  }

  onlyNumbers(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;

    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  onPasteName(event: ClipboardEvent): void {
    const pastedInput = event.clipboardData?.getData('text') || '';

    if (!/^[A-Za-z ]+$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  onPasteNumber(event: ClipboardEvent): void {
    const pastedInput = event.clipboardData?.getData('text') || '';

    if (!/^\d+$/.test(pastedInput)) {
      event.preventDefault();
    }
  }

  // ---------- Helpers ----------

  private getJwtToken(response: any): string {
    return response?.data?.token ||
      response?.data?.jwtToken ||
      response?.data?.accessToken ||
      response?.token ||
      response?.jwtToken ||
      response?.accessToken ||
      '';
  }

  private saveReportSession(): void {
    localStorage.setItem('wealthLensAuthToken', this.authToken);
    localStorage.setItem('wealthLensReportUser', JSON.stringify({
      name: this.name,
      pan: this.maskPan(this.pan),
      rawPan: this.pan,
      email: this.email,
      mobile: this.mobile,
      transactionId: this.transactionId,
      reportType: this.reportType,
      rmLeadCode: this.rmLeadCode,
    }));
  }

  private maskPan(pan: string): string {
    if (!pan || pan.length < 4) {
      return 'XXXXX4321F';
    }

    return `XXXXX${pan.slice(-5)}`;
  }

  private openReportPage(): void {
    // Navigate within the same tab using Angular Router — no pop-up blocker issues.
    this.router.navigate([this.reportRoute]);
  }
}
